// src/layouts/experiences/index.js
import React from "react";
import {
  Grid,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography"; // Add this import
import Icon from "@mui/material/Icon";
import experienceNameTable from "layouts/experiences/data/experienceNameTable";
import { db, storage } from "../../firebase";
import { collection, addDoc, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import ExperienceForm from "./components/ExperienceForm";
import Detail from "./components/Detail";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

function Experiences() {
  const { columns, rows } = experienceNameTable();
  const [experienceModal, setExperienceModal] = React.useState(false);
    const [experienceNotification, setExperienceNotification] = React.useState(false);
    const [detailModalOpen, setDetailModalOpen] = React.useState(false); // New state for modal
    const [selectedExperienceId, setSelectedExperienceId] = React.useState(null); // Selected experience ID

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [imageProgress, setImageProgress] = React.useState(0);
  const [imageProgressValue, setImageProgressValue] = React.useState(0);
  const [experienceFile, setExperienceFile] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});

  const [experienceData, setExperienceData] = React.useState({
    id: "",
    description: "",
    version: "",
    contentBucket: "",
    beacons: {},
    locales: {},
    titles: {},
    mode: "",
  });

  // Experience File Upload
  React.useEffect(() => {
    const uploadExperienceFile = () => {
      const name = experienceFile.name;
      const storageRef = ref(storage, `Experiences/${name}`);
      const uploadTask = uploadBytesResumable(storageRef, experienceFile);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setImageProgress(progress);
          setImageProgressValue(progress);
        },
        (error) => {
          console.log("ERROR == ", error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setExperienceData((prev) => ({
              ...prev,
              image: downloadURL,
            }));
          });
        }
      );
    };
    experienceFile && uploadExperienceFile();
  }, [experienceFile]);

  const handleDetailOpen = (id) => {
    setSelectedExperienceId(id); // Store the selected experience ID
    setDetailModalOpen(true); // Open the modal
  };
  
  const handleDetailClose = () => {
    setDetailModalOpen(false); // Close the modal
    setSelectedExperienceId(null); // Reset the selected ID
  };
  

const onAddExperience = async (e) => {
  e.preventDefault();

  if (!validateFields()) {
    console.log("Validation failed:", fieldErrors);
    return; // Stop execution if validation fails
  }

  console.log("experienceData == ", experienceData);

  try {
    setLoading(true);

    const newExperienceData = {
      id: experienceData.id.toLowerCase().replace(/\s+/g, "").trim(),
      description: experienceData.description,
      version: experienceData.version,
      contentBucket: experienceData.contentBucket,
      beacons: experienceData.beacons,
      locales: experienceData.locales,
      titles: experienceData.titles,
      mode: experienceData.mode,
      image: experienceData.image || null, // Optional image
    };

    const docId = newExperienceData.id;
    const docRef = doc(db, "Experiences", docId);
    await setDoc(docRef, newExperienceData);

    experienceModalClose();
    experienceNotificationOpen();
    setExperienceData({
      id: "",
      description: "",
      version: "",
      contentBucket: "",
      beacons: {},
      locales: {},
      titles: {},
      mode: "",
      image: "", // Reset the image field
    });
    setImageProgress(0);
    setImageProgressValue(0);
  } catch (error) {
    console.error("Error saving experience:", error);
    setError(error.message);
    setLoading(false);
  }
};

  const validateFields = () => {
    const errors = {};
  
    if (!experienceData.id) errors.id = "Experience ID is required";
    if (!experienceData.description) errors.description = "Description is required";
    if (!experienceData.version) errors.version = "Version is required";
    if (!experienceData.contentBucket) {
      errors.contentBucket = "Content Bucket URL is required";
    } else if (
      !/^(https?|http|gs):\/\/.+|^gs:\/\/.+|^www\..+\..+$/.test(experienceData.contentBucket)
    ) {
      errors.contentBucket = "Content Bucket URL is invalid";
    }
    if (Object.keys(experienceData.titles).length === 0) {
      errors.titles = "At least one title is required";
    }
    if (Object.keys(experienceData.beacons).length === 0) {
      errors.beacons = "At least one beacon is required";
    }
  
    setFieldErrors(errors); // Update state for errors
    return Object.keys(errors).length === 0;
  };
  
  
  

  const experienceModalOpen = () => setExperienceModal(true);
  const experienceModalClose = () => {
    setExperienceModal(false);
    setLoading(false);
    setError("");
    setImageProgress(0);
    setImageProgressValue(0);
  };
  const experienceNotificationOpen = () => setExperienceNotification(true);
  const experienceNotificationClose = () => setExperienceNotification(false);

  return (
    <>
      <MDSnackbar
        color="success"
        icon="check"
        title="Successfully Added"
        open={experienceNotification}
        onClose={experienceNotificationClose}
        close={experienceNotificationClose}
      />
      <BootstrapDialog
        onClose={experienceModalClose}
        aria-labelledby="customized-dialog-title"
        open={experienceModal}
        maxWidth="md" // Control the size of the sub-window
        fullWidth
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={experienceModalClose}>
          <Typography variant="h3" color="secondary.main" sx={{ pt: 1, textAlign: "center" }}>
            Add Experience
          </Typography>
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <ExperienceForm
            experienceData={experienceData}
            setExperienceData={setExperienceData}
            experienceFile={experienceFile}
            setExperienceFile={setExperienceFile}
            imageProgress={imageProgress}
            imageProgressValue={imageProgressValue}
            error={error}
            setError={setError}
            loading={loading}
            onAddExperience={onAddExperience}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          {loading ? (
            <CircularProgress
              size={30}
              sx={{
                color: green[500],
              }}
            />
          ) : (
            <MDButton variant="contained" color="info" type="submit" onClick={onAddExperience}>
              Save
            </MDButton>
          )}
        </DialogActions>
      </BootstrapDialog>

      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDBox>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDBox
                      pt={2}
                      pb={2}
                      px={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" fontWeight="medium" color="white">
                        All Experiences
                      </MDTypography>
                      <MDButton
                        variant="gradient"
                        color="light"
                        onClick={() => {
                          experienceModalOpen();
                        }}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        &nbsp;ADD EXPERIENCE
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  <MDBox pt={3}>
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default Experiences;